
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { CandidateForm, CandidateFormData } from '@/components/candidates/CandidateForm';
import { useToast } from '@/hooks/use-toast';
import { Sentry, isSentryEnabled } from '@/sentry';

export default function EditCandidatePage() {
  const { candidateId } = useParams<{ candidateId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: candidate, isLoading } = useQuery({
    queryKey: ['candidate', candidateId],
    queryFn: async () => {
      if (!candidateId) throw new Error('Candidate ID is required');
      
      const { data, error } = await supabase
        .from('candidates')
        .select('*')
        .eq('id', candidateId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!candidateId,
  });

  const handleSubmit = async (data: CandidateFormData) => {
    if (!candidateId) return;
    
    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('candidates')
        .update({
          full_name: data.full_name,
          email: data.email,
          phone: data.phone,
          branch: data.branch,
          qualification: data.qualification,
          cgpa: data.cgpa,
          college_university: data.college_university,
          college_id: data.college_id || null,
          job_position_id: data.job_position_id || null,
          latest_semester_score: data.latest_semester_score,
          github_url: data.github_url,
          linkedin_url: data.linkedin_url,
          permanent_address: data.permanent_address,
          permanent_city: data.permanent_city,
          current_address: data.current_address,
          current_city: data.current_city,
          position_applied: data.position_applied,
          hobbies_interests: data.hobbies_interests,
          key_skills: data.key_skills,
          technologies: data.technologies,
          status: data.status,
          current_round: data.current_round,
        })
        .eq('id', candidateId);

      if (error) throw error;

      toast({
        title: "Candidate Updated",
        description: "The candidate has been updated successfully.",
      });

      navigate('/admin/candidates');
    } catch (error) {
      console.error('Error updating candidate:', error);
      if (isSentryEnabled) Sentry.captureException(error);
      toast({
        title: "Error",
        description: "Failed to update candidate. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="text-center">Loading candidate details...</div>
      </div>
    );
  }

  if (!candidate) {
    return (
      <div className="space-y-6">
        <div className="text-center">Candidate not found</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          onClick={() => navigate('/admin/candidates')}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Candidates
        </Button>
        <div>
          <h1 className="text-3xl font-bold">Edit Candidate</h1>
          <p className="text-muted-foreground">{candidate.full_name}</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Candidate Information</CardTitle>
          <CardDescription>
            Update the candidate details
          </CardDescription>
        </CardHeader>
        <CardContent>
          <CandidateForm 
            candidate={candidate} 
            onSubmit={handleSubmit} 
            isSubmitting={isSubmitting} 
          />
        </CardContent>
      </Card>
    </div>
  );
}
